// src/components/analytics/CampaignAlertsPage.tsx
// CM-2a — inbox de alerts generados por el anomaly detector
// (CampaignAlert — threshold-based, no-LLM, cada 30min).
//
// Diferente a `OptimizerAlertsPage` que consume LLM-based suggestions
// de ai_marketing.OptimizationLog (apply/dismiss actions). Este
// es read + acknowledge only.

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, Check, Loader2, RefreshCw,
} from 'lucide-react';

import {
  ApiError, listAlerts, resolveAlert,
  type CampaignAlert,
} from '@/lib/api_analytics';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;
interface Props { addNotification?: Notifier }

const SEVERITY_BADGE: Record<string, string> = {
  info: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  warning: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  critical: 'bg-red-500/15 text-red-300 border-red-500/30',
};

const KIND_LABEL: Record<string, string> = {
  cpm_spike: 'CPM spike',
  ctr_crash: 'CTR crash',
  spend_stall: 'Sin gasto',
  stalled_campaign: 'Campaña estancada',
  budget_exhausted: 'Budget agotado',
  creative_fatigue: 'Fatiga creativa',
  custom: 'Custom',
};

export function CampaignAlertsPage({ addNotification }: Props) {
  const [alerts, setAlerts] = useState<CampaignAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [unresolved, setUnresolved] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAlerts({ unresolved_only: unresolved });
      setAlerts(res.items);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No cargaron alertas', message: msg });
    } finally {
      setLoading(false);
    }
  }, [unresolved, addNotification]);

  useEffect(() => { load(); }, [load]);

  const ack = async (id: number) => {
    setBusy(id);
    try {
      await resolveAlert(id);
      addNotification?.({ type: 'success', title: 'Alerta resuelta' });
      await load();
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : e?.message || 'Error';
      addNotification?.({ type: 'error', title: 'No pudimos resolver', message: msg });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Alertas de campañas</h1>
          <p className="text-xs text-white/50">
            Detectadas cada 30 min — CPM spikes, CTR crashes, spend stalls
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer">
            <input
              type="checkbox"
              checked={unresolved}
              onChange={e => setUnresolved(e.target.checked)}
            /> Solo abiertas
          </label>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70"
            title="Refrescar"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
        </div>
      </div>

      {!alerts.length && !loading && (
        <div className="o-card text-center text-sm text-white/60 py-10">
          🎉 Sin alertas {unresolved ? 'abiertas' : ''}. Todo en verde.
        </div>
      )}

      <div className="space-y-2">
        {alerts.map(a => (
          <div key={a.id} className={`o-card flex items-start gap-3 border ${SEVERITY_BADGE[a.severity] || ''}`}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-[11px] uppercase px-1.5 py-0.5 rounded border border-current">
                  {KIND_LABEL[a.kind] || a.kind}
                </span>
                <span className="text-white/80 text-sm font-medium truncate">
                  {a.campaign_name}
                </span>
                {a.resolved_at && (
                  <span className="text-[10px] text-white/40">
                    · resuelta ({a.resolved_by || 'unknown'})
                  </span>
                )}
              </div>
              <div className="text-sm text-white/75">{a.message}</div>
              <div className="text-[10px] text-white/40 mt-1">
                {new Date(a.detected_at).toLocaleString('es-MX')}
              </div>
            </div>
            {!a.resolved_at && (
              <button
                onClick={() => ack(a.id)}
                disabled={busy === a.id}
                className="shrink-0 px-3 py-1.5 text-xs rounded-lg bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 flex items-center gap-1.5"
              >
                {busy === a.id
                  ? <Loader2 size={12} className="animate-spin" />
                  : <Check size={12} />}
                Resolver
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default CampaignAlertsPage;
